"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import DownArrowIcon from "@/public/down-arrow.svg";
import UpArrowIcon from "@/public/up-arrow.svg";
import { cn } from "@/lib/utils";

const languages = [
    { code: "DE", locale: "de", label: "Deutsch" },
    { code: "EN", locale: "en", label: "English" },
    { code: "FR", locale: "fr", label: "Français" },
] as const;

export type LanguageOption = (typeof languages)[number];

const DEFAULT_LOCALE = "de";

const SUPPORTED_LOCALES = languages.map((lang) => lang.locale);

function stripLocale(pathname: string) {
    const segments = pathname.split("/").filter(Boolean);

    if (
        segments.length > 0 &&
        SUPPORTED_LOCALES.includes(segments[0] as LanguageOption["locale"])
    ) {
        segments.shift();
    }

    return "/" + segments.join("/");
}

function getSelectedLanguage(pathname: string): LanguageOption {
    const segments = pathname.split("/").filter(Boolean);
    const potentialLocale = segments.length > 0 ? segments[0] : null;

    const foundLanguage = languages.find((lang) => lang.locale === potentialLocale);

    return (
        foundLanguage || languages.find((lang) => lang.locale === DEFAULT_LOCALE) || languages[0]
    );
}

function getLocalizedHref(pathname: string, locale: LanguageOption["locale"]) {
    const basePath = stripLocale(pathname);

    if (locale === DEFAULT_LOCALE) {
        return basePath;
    }
    console.log(locale, "pathname");

    return basePath === "/" ? `/${locale}` : `/${locale}${basePath}`;
}

interface LanguagePickerProps {
    className?: string;
    placement?: "top" | "bottom";
}

export function LanguagePicker({ className, placement = "bottom" }: LanguagePickerProps) {
    const pathname = usePathname();

    return (
        <LanguagePickerContent
            key={pathname}
            pathname={pathname}
            className={className}
            placement={placement}
        />
    );
}

interface LanguagePickerContentProps {
    pathname: string;
    className?: string;
    placement?: "top" | "bottom";
}

function LanguagePickerContent({
    pathname,
    className,
    placement = "bottom",
}: LanguagePickerContentProps) {
    const router = useRouter();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const selected = getSelectedLanguage(pathname);

    useEffect(() => {
        function onClickOutside(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    return (
        <div ref={ref} className={cn("relative inline-block", className)}>
            {open && (
                <div
                    className={cn(
                        "absolute right-0 z-50 overflow-hidden rounded-xl border border-black/5 bg-language-bg shadow-lg",
                        placement === "top" ? "bottom-full mb-2" : "top-full mt-2",
                    )}
                >
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            type="button"
                            onClick={() => {
                                const href = getLocalizedHref(pathname, lang.locale);

                                setOpen(false);

                                if (href !== pathname) {
                                    router.replace(href, { scroll: false });
                                }
                            }}
                            className={cn(
                                "block w-full cursor-pointer px-5 py-3 text-left text-sm font-medium text-language-fg transition hover:bg-black/5",
                                lang.locale === selected.locale && "bg-black/5",
                            )}
                        >
                            {lang.label}
                        </button>
                    ))}
                </div>
            )}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="menu"
                aria-label="Select language"
                className="inline-flex cursor-pointer items-center gap-2 rounded-[10px] bg-language-bg px-4 py-3 text-sm font-medium text-language-fg"
            >
                {selected.code}
                <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center">
                    {open ? (
                        <UpArrowIcon className="w-3 h-1.5 [&_path]:stroke-ink" aria-hidden="true" />
                    ) : (
                        <DownArrowIcon
                            className="w-3 h-1.5 [&_path]:stroke-ink"
                            aria-hidden="true"
                        />
                    )}
                </span>
            </button>
        </div>
    );
}
